"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Youtube, Facebook, Instagram, Twitter, Github } from "lucide-react";
import { Container } from "@/components/ui/container";
import { BuddifyLogo } from "@/components/ui/buddify-logo";
import { siteConfig } from "@/lib/constants";
import { ThemeSelect } from "./theme-select";

const socialLinks = [
  {
    name: "YouTube",
    href: siteConfig.social.youtube,
    icon: Youtube,
  },
  {
    name: "Facebook",
    href: siteConfig.social.facebook,
    icon: Facebook,
  },
  {
    name: "Instagram",
    href: siteConfig.social.instagram,
    icon: Instagram,
  },
  { name: "X", href: siteConfig.social.x, icon: Twitter },
  { name: "GitHub", href: siteConfig.social.github, icon: Github },
];

const footerNavigation = {
  products: [
    { name: "Brand Partnerships", href: "/brand-partnerships" },
    { name: "Buddy AI Assistant", href: "/buddy-ai-assistant" },
    { name: "Marketplace", href: "/marketplace" },
  ],
  company: [
    { name: "Partners", href: "/partners" },
    { name: "Pricing", href: "/pricing" },
    { name: "Contact", href: "/contact" },
    {
      name: "Help Center",
      href: "https://support.buddify.app",
      external: true,
    },
    { name: "Legal Center", href: "/legal" },
  ],
  community: [
    { name: "Explore independent brands", href: "/community" },
    { name: "Search partner brands", href: "/community/search" },
    { name: "Browse categories", href: "/community#categories" },
    { name: "Featured collections", href: "/community#collections" },
    { name: "List your brand", href: "/brand-partnerships" },
  ],
};

export function Footer() {
  const pathname = usePathname();
  if (pathname === "/dashboard" || pathname.startsWith("/dashboard/")) {
    return (
      <footer className="border-t border-retro-line/20 bg-retro-paper py-5">
        <Container className="flex justify-end">
          <ThemeSelect />
        </Container>
      </footer>
    );
  }
  return (
    <footer className="bg-panel text-muted">
      <Container>
        <div className="py-6 md:py-14">
          {/* Main Footer */}
          <div className="flex flex-col md:flex-row justify-between gap-12">
            {/* Brand - Left aligned */}
            <div className="max-w-xs">
              <div className="mb-4">
                <BuddifyLogo variant="auto" />
              </div>
              <p className="text-sm text-muted leading-relaxed mb-6">
                AI-driven commerce. Built around your store.
              </p>
              <p className="text-sm text-muted mb-4">© {new Date().getFullYear()} Buddify</p>
              {/* Social Icons */}
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted hover:text-ink transition-colors"
                    aria-label={social.name}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation columns - Right aligned */}
            <div className="grid grid-cols-2 gap-8 md:grid-cols-3 md:gap-12">
              {/* Products */}
              <div>
                <h3 className="font-semibold text-ink mb-4">Products</h3>
                <ul className="space-y-3">
                  {footerNavigation.products.map((item) => (
                    <li key={item.name}>
                      <Link
                        href={item.href}
                        className="text-sm text-muted hover:text-ink transition-colors"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Community */}
              <div>
                <h3 className="font-semibold text-ink mb-4">Community</h3>
                <ul className="space-y-3">
                  {footerNavigation.community.map((item) => (
                    <li key={item.name}>
                      <Link
                        href={item.href}
                        className="text-sm text-muted hover:text-ink transition-colors"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company */}
              <div>
                <h3 className="font-semibold text-ink mb-4">Company</h3>
                <ul className="space-y-3">
                  {footerNavigation.company.map((item) => (
                    <li key={item.name}>
                      {item.external ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-muted hover:text-ink transition-colors"
                        >
                          {item.name}
                        </a>
                      ) : (
                        <Link
                          href={item.href}
                          className="text-sm text-muted hover:text-ink transition-colors"
                        >
                          {item.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Privacy notice and appearance */}
          <div className="mt-12 pt-8 border-t border-line flex flex-col-reverse items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted">
              We do not sell any information that brands provide on Buddify
            </p>
            <ThemeSelect />
          </div>
        </div>
      </Container>
    </footer>
  );
}
